/**
 * GTMLoader — runtime Google Tag Manager loader for the SPA.
 *
 *   1. Fetches /api/seo/analytics on mount.
 *   2. If enabled + has a GTM id, injects the standard GTM <script> plus a
 *      <noscript> iframe fallback exactly once per page load. The script is
 *      appended to <head>, the iframe to the top of <body>, matching the
 *      integration snippet Google publishes.
 *   3. Watches route changes via useLocation and pushes a `virtualPageview`
 *      event to window.dataLayer so SPA navigations count in GA4, not just
 *      the first-paint URL.
 *
 * The component renders nothing. Everything happens as side-effects so it
 * can sit anywhere inside the app tree.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { readConsent, hasValidConsent, toConsentModeV2, subscribe as onConsentChange } from "../lib/consent";

const HEAD_SCRIPT_ID = "trex-gtm-head";
const NOSCRIPT_IFRAME_ID = "trex-gtm-noscript";

let cachedConfig = null;
let cachedConsentCfg = null;

async function loadConfig() {
  if (cachedConfig) return cachedConfig;
  try {
    const { data } = await api.get("/seo/analytics");
    cachedConfig = data;
    return data;
  } catch {
    return null;
  }
}

async function loadConsentConfig() {
  if (cachedConsentCfg) return cachedConsentCfg;
  try {
    const { data } = await api.get("/seo/consent");
    cachedConsentCfg = data;
    return data;
  } catch {
    return null;
  }
}

/**
 * Push the *default* Google Consent Mode v2 signals BEFORE gtm.js loads.
 * Google's spec: any tag firing between page load and the user's consent
 * choice will see these defaults. When the user later clicks Accept in the
 * CookieBanner, an "update" is pushed to grant the categories they chose.
 */
function pushConsentDefaults(consentCfg) {
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }

  const existing = readConsent();
  const bannerEnabled = consentCfg && consentCfg.banner_enabled;
  const bannerVersion = (consentCfg && consentCfg.version) || 1;

  // If we already have a valid user choice, use it. Otherwise fall back to
  // the admin's default policy (opt_in = deny, opt_out = grant).
  let signals;
  if (hasValidConsent(existing, bannerVersion)) {
    signals = toConsentModeV2(existing);
  } else if (bannerEnabled && (!consentCfg || consentCfg.default_policy !== "opt_out")) {
    signals = toConsentModeV2({ analytics: false, marketing: false });
  } else {
    // Banner disabled OR opt-out policy: grant analytics by default. Marketing
    // stays denied unless the visitor explicitly opts in.
    signals = toConsentModeV2({ analytics: true, marketing: false });
  }
  gtag("consent", "default", { ...signals, wait_for_update: 500 });
}

function ensureDataLayer(env) {
  window.dataLayer = window.dataLayer || [];
  // First event GTM expects — sets timing baseline.
  window.dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
    env: env || "production",
  });
}

function injectGTMScript(gtmId) {
  if (document.getElementById(HEAD_SCRIPT_ID)) return;
  const s = document.createElement("script");
  s.id = HEAD_SCRIPT_ID;
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  document.head.appendChild(s);
}

function injectNoscriptFallback(gtmId) {
  if (document.getElementById(NOSCRIPT_IFRAME_ID)) return;
  // Users with JS disabled: append an iframe fallback so at least a pageview
  // is recorded. Google's official snippet places this immediately after
  // <body>; we prepend it to keep behaviour identical.
  const ns = document.createElement("noscript");
  ns.id = NOSCRIPT_IFRAME_ID;
  ns.innerHTML =
    `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}"` +
    ` height="0" width="0" style="display:none;visibility:hidden" title="Google Tag Manager"></iframe>`;
  document.body.insertBefore(ns, document.body.firstChild);
}

function shouldSkip(config) {
  if (!config || !config.enabled || !config.gtm_id) return true;
  if (config.production_only) {
    // Skip on obviously non-production hosts (localhost, *.preview.*).
    const host = window.location.hostname;
    const isPreview = host.includes(".preview.") || host === "localhost" || host === "127.0.0.1";
    if (isPreview) return true;
  }
  return false;
}

export default function GTMLoader() {
  const { pathname, search } = useLocation();
  const loaded = useRef(false);
  const configRef = useRef(null);

  // First mount: fetch config + inject snippet.
  useEffect(() => {
    let alive = true;
    Promise.all([loadConfig(), loadConsentConfig()]).then(([cfg, consentCfg]) => {
      if (!alive) return;
      configRef.current = cfg;
      if (shouldSkip(cfg)) return;
      // 1. Init dataLayer + push consent DEFAULT signals BEFORE gtm.js loads
      //    so any tag firing on page load respects the visitor's preference.
      ensureDataLayer(cfg.environment);
      pushConsentDefaults(consentCfg);
      // 2. Now load GTM and the noscript fallback.
      injectGTMScript(cfg.gtm_id);
      injectNoscriptFallback(cfg.gtm_id);
      loaded.current = true;
    });

    // Re-emit a "consent update" when the visitor changes their mind so any
    // tag added after page load respects the new state.
    const unsub = onConsentChange((next) => {
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      gtag("consent", "update", toConsentModeV2(next || {}));
    });

    return () => { alive = false; unsub(); };
  }, []);

  // Subsequent route changes: push a virtualPageview so SPA navigation shows
  // in GA. `pathname + search` is what most analysts want as the "page path".
  useEffect(() => {
    const cfg = configRef.current;
    if (!cfg || !cfg.enabled || !cfg.spa_pageviews) return;
    // dataLayer may not exist yet if the initial fetch is still in flight;
    // fall through — GTM's own gtm.js load event covers the first paint.
    if (!window.dataLayer) return;
    window.dataLayer.push({
      event: "virtualPageview",
      page_path: pathname + (search || ""),
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
}

/** Push an arbitrary event to dataLayer. Safe no-op if GTM never loaded. */
export function pushDataLayerEvent(event, params = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

/** Force the config cache to reload (used by the admin panel after PATCH). */
export function invalidateAnalyticsCache() {
  cachedConfig = null;
  cachedConsentCfg = null;
}
